import React, { useEffect, useMemo, useState } from 'react';
import { View, Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import SelectInput from '@/components/General/inputs/SelectInput';
import InputSection from '@/components/General/inputs/InputSection';
import Checkbox from '@/components/General/inputs/Checkbox';
import { saveTrainingTerm } from '@/data/training';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Query } from '@/utils/react-query/Queries';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import Toast from 'react-native-toast-message';
import { useLocalSearchParams, useRouter } from 'expo-router';
import ModalWrapper from '@/components/General/ModalWrapper';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';

export default function EditTrainingTermModal() {
    const { moduleID, termID } = useLocalSearchParams();
    const isEditing = !!termID;

    const queryClient = useQueryClient();
    const router = useRouter();

    const { data: trainingModule } = Query.trainingModules.byId({ id: moduleID, options: { enabled: !!moduleID } });

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    // rows: { [classID]: { include, scheduledAt, teacherID, instanceID } }
    const [rowsByClassID, setRowsByClassID] = useState({});

    const allClasses = useMemo(
        () => [...(trainingModule?.Classes || [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
        [trainingModule]
    );
    const teacherOptions = (trainingModule?.Teachers || []).map(t => ({
        label: `${t.firstName} ${t.lastName}`,
        value: t.id,
    }));
    const terms = trainingModule?.Terms || [];
    const editingTerm = isEditing ? terms.find(t => t.id === termID) : null;

    // Initialize rows from module data (edit mode prefills instances in this term).
    useEffect(() => {
        if (!trainingModule) return;
        const next = {};
        for (const cls of allClasses) {
            const inst = (cls.Instances || []).find(i => i.termID === (termID || undefined));
            if (isEditing && inst) {
                next[cls.id] = {
                    include: true,
                    scheduledAt: inst.scheduledAt ? new Date(inst.scheduledAt) : null,
                    teacherID: inst.teacherID || null,
                    instanceID: inst.id,
                };
            } else {
                next[cls.id] = { include: true, scheduledAt: null, teacherID: null, instanceID: null };
            }
        }
        setRowsByClassID(next);
    }, [trainingModule, termID, isEditing]);

    useEffect(() => {
        if (editingTerm) {
            setName(editingTerm.name || '');
            setDescription(editingTerm.description || '');
        }
    }, [editingTerm]);

    const updateRow = (classID, patch) => {
        setRowsByClassID(prev => ({ ...prev, [classID]: { ...prev[classID], ...patch } }));
    };

    const saveMutation = useMutation({
        mutationFn: () => saveTrainingTerm({
            termID: isEditing ? termID : null,
            moduleID,
            name,
            description: description || null,
            rows: allClasses.map(cls => {
                const r = rowsByClassID[cls.id] || { include: false, scheduledAt: null, teacherID: null, instanceID: null };
                return {
                    classID: cls.id,
                    include: !!r.include,
                    scheduledAt: r.scheduledAt instanceof Date ? r.scheduledAt.toISOString() : r.scheduledAt,
                    teacherID: r.teacherID || null,
                    instanceID: r.instanceID || null,
                };
            }),
        }),
        onSuccess: () => {
            Toast.show({ type: 'success', text2: isEditing ? 'Term updated successfully!' : 'Term created successfully!' });
            queryClient.invalidateQueries({ queryKey: QueryKeys.trainingModules.byId(moduleID) });
            queryClient.invalidateQueries({ queryKey: ['trainingModules'] });
            queryClient.invalidateQueries({ queryKey: QueryKeys.trainingTerms.byModuleId(moduleID) });
            router.dismiss?.() || router.back();
        },
    });

    const handleSave = () => {
        if (!name?.trim()) {
            Toast.show({ type: 'error', text2: 'Term name is required' });
            return;
        }
        // Validation: every included row must have a date.
        const missingDateClassNames = allClasses
            .filter(cls => rowsByClassID[cls.id]?.include && !rowsByClassID[cls.id]?.scheduledAt)
            .map(cls => cls.name);
        if (missingDateClassNames.length > 0) {
            Toast.show({
                type: 'error',
                text2: `Date required for: ${missingDateClassNames.join(', ')}`,
            });
            return;
        }
        saveMutation.mutate();
    };

    return (
        <ModalWrapper
            title={isEditing ? "Edit Term" : "Schedule a New Term"}
            onConfirm={handleSave}
            loading={saveMutation.isPending}
        >
            <KeyboardAwareScrollView
                contentContainerStyle={styles.container}
                keyboardShouldPersistTaps="handled"
            >
                <InputSection title="Term Details">
                    <Input
                        title="Term Name"
                        required
                        value={name}
                        onUpdate={setName}
                        testID="term-name-input"
                    />
                    <Input
                        title="Description (optional)"
                        multiline
                        value={description}
                        onUpdate={setDescription}
                        testID="term-description-input"
                    />
                </InputSection>

                <InputSection title="Classes" description="Set a date and teacher for each class you want to include this term.">
                    {allClasses.map(cls => {
                        const row = rowsByClassID[cls.id] || { include: true, scheduledAt: null, teacherID: null };
                        return (
                            <View key={cls.id} style={styles.classRow} testID={`term-class-row-${cls.id}`}>
                                <View style={styles.classRowHeader}>
                                    <Checkbox
                                        checked={!!row.include}
                                        onChange={(v) => updateRow(cls.id, { include: v })}
                                        testID={`term-class-checkbox-${cls.id}`}
                                    />
                                    <View style={{ flex: 1 }}>
                                        <Text style={styles.className}>{cls.name}</Text>
                                        {cls.description ? <Text style={styles.classDescription}>{cls.description}</Text> : null}
                                    </View>
                                </View>
                                <View style={[styles.classRowInputs, !row.include && styles.classRowInputsDisabled]} pointerEvents={row.include ? 'auto' : 'none'}>
                                    <DatePickerInput
                                        title="Date & Time"
                                        mode="datetime"
                                        value={row.scheduledAt}
                                        onUpdate={(v) => updateRow(cls.id, { scheduledAt: v })}
                                    />
                                    <SelectInput
                                        title="Teacher (optional)"
                                        placeholder="No teacher assigned"
                                        value={row.teacherID}
                                        options={teacherOptions}
                                        onUpdate={(value) => updateRow(cls.id, { teacherID: value || null })}
                                    />
                                </View>
                            </View>
                        );
                    })}
                </InputSection>
            </KeyboardAwareScrollView>
        </ModalWrapper>
    );
}

const styles = StyleSheet.create(({ colors, fonts, spacing }) => ({
    container: {
        gap: spacing.md,
    },
    classRow: {
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: 12,
        padding: spacing.md,
        gap: spacing.sm,
        marginBottom: spacing.md,
    },
    classRowHeader: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: spacing.sm,
    },
    className: {
        fontSize: 16,
        fontFamily: fonts.medium,
        color: colors.text,
    },
    classDescription: {
        fontSize: 13,
        fontFamily: fonts.body,
        color: colors.textTertiary,
        marginTop: 2,
    },
    classRowInputs: {
        marginTop: spacing.sm,
        gap: spacing.sm,
    },
    classRowInputsDisabled: {
        opacity: 0.4,
    },
}));
